// Shared helper to classify a failed fetch/apicFetch call (or thrown error) into one
// of the categories called out in the "Specific error messages for Provider Search
// 2.0" story: timeout, serverError (5xx), gatewayUnavailable (4xx, excluding 429),
// rateLimited (429), or unknown.
//
// Usage:
//   try {
//     const response = await apicFetch(url);
//     if (!response.ok) {
//       const classification = classifyApiError({ response });
//       ...
//     }
//   } catch (err) {
//     const classification = classifyApiError({ error: err });
//     ...
//   }

export const API_ERROR_KINDS = {
  TIMEOUT: 'timeout',
  SERVER_ERROR: 'serverError',
  GATEWAY_UNAVAILABLE: 'gatewayUnavailable',
  RATE_LIMITED: 'rateLimited',
  UNKNOWN: 'unknown',
};

/**
 * @param {Object} params
 * @param {Response} [params.response] - A fetch/apicFetch Response object (may be !ok).
 * @param {Error} [params.error] - A caught error/exception (e.g. network failure, abort).
 * @returns {{ kind: string, status: number|null, message: string }}
 */
export const classifyApiError = ({ response, error } = {}) => {
  if (response) {
    const status = response.status;

    if (status === 429) {
      return { kind: API_ERROR_KINDS.RATE_LIMITED, status, message: 'Rate limited (429)' };
    }
    if (status >= 500) {
      return { kind: API_ERROR_KINDS.SERVER_ERROR, status, message: `Server error (${status})` };
    }
    if (status >= 400) {
      return { kind: API_ERROR_KINDS.GATEWAY_UNAVAILABLE, status, message: `Gateway/client error (${status})` };
    }

    return { kind: API_ERROR_KINDS.UNKNOWN, status, message: `Unexpected status (${status})` };
  }

  if (error) {
    const name = error?.name || '';
    const msg = (error?.message || '').toLowerCase();

    // Many call sites throw `new Error(\`HTTP ${status}: ${statusText}\`)` rather than
    // passing the raw Response object through — extract the status code if present so
    // those still classify correctly instead of falling through to UNKNOWN.
    const httpMatch = (error?.message || '').match(/HTTP\s+(\d{3})/i);
    if (httpMatch) {
      const status = parseInt(httpMatch[1], 10);
      if (status === 429) {
        return { kind: API_ERROR_KINDS.RATE_LIMITED, status, message: error.message };
      }
      if (status >= 500) {
        return { kind: API_ERROR_KINDS.SERVER_ERROR, status, message: error.message };
      }
      if (status >= 400) {
        return { kind: API_ERROR_KINDS.GATEWAY_UNAVAILABLE, status, message: error.message };
      }
    }

    if (name === 'AbortError' || msg.includes('timeout') || msg.includes('timed out')) {
      return { kind: API_ERROR_KINDS.TIMEOUT, status: null, message: error.message || 'Request timed out' };
    }
    if (msg.includes('failed to fetch') || msg.includes('network')) {
      return { kind: API_ERROR_KINDS.GATEWAY_UNAVAILABLE, status: null, message: error.message || 'Network error' };
    }

    return { kind: API_ERROR_KINDS.UNKNOWN, status: null, message: error.message || 'Unknown error' };
  }

  return { kind: API_ERROR_KINDS.UNKNOWN, status: null, message: 'Unknown error' };
};

// Returns true if the classification represents one of the four conditions this
// story says should surface the APIC/MCS backend error banner (timeout, 5xx,
// gateway unavailable 4xx, or 429 rate-limited).
export const isUserFacingApiError = (classification) => {
  if (!classification) return false;
  return [
    API_ERROR_KINDS.TIMEOUT,
    API_ERROR_KINDS.SERVER_ERROR,
    API_ERROR_KINDS.GATEWAY_UNAVAILABLE,
    API_ERROR_KINDS.RATE_LIMITED,
  ].includes(classification.kind);
};
